import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import {
  BookOpen, GraduationCap, Brain, Target, Clock, Flame, RotateCcw,
  ArrowLeft, Loader2, ChevronRight, Cloud, Mic, FileText, Trophy, Calendar, Sparkles
} from 'lucide-react';
import { motion } from 'framer-motion';
import Tip from '../components/Tip';
import ClassNetworkStrip from '../components/ClassNetworkStrip';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SubjectOverviewPage = () => {
  const { subjectId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scheduleForm, setScheduleForm] = useState({ exam_date: '', exam_title: '', hours: 2 });
  const [generating, setGenerating] = useState(false);
  const [scheduleResult, setScheduleResult] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/subjects/${subjectId}/overview`);
        setData(res.data);
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    load();
  }, [subjectId]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" /></div>;
  if (!data) return <div className="text-center py-20 text-[var(--isp-muted)]">Subject not found</div>;

  const { subject, stats, recent_lectures, recent_exams, storage } = data;
  const hours = Math.floor(stats.total_study_minutes / 60);
  const mins = stats.total_study_minutes % 60;

  const generateAISchedule = async () => {
    if (!scheduleForm.exam_date) { toast.error('Set an exam date'); return; }
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/schedule/ai-optimize`, {
        subject_id: subjectId,
        exam_date: scheduleForm.exam_date,
        exam_title: scheduleForm.exam_title || `${subject.name} Exam`,
        study_hours_per_day: scheduleForm.hours,
      });
      setScheduleResult(res.data);
      toast.success(`AI schedule created: ${res.data.total_events} sessions!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate schedule');
    }
    setGenerating(false);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="subject-overview">
      {/* Breadcrumb + Header */}
      <div>
        <Link to="/subjects" className="inline-flex items-center gap-1 text-sm text-[#002FA7] hover:underline mb-3">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Subjects
        </Link>
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl flex items-center justify-center text-white text-2xl font-bold" style={{ backgroundColor: subject.color }}>
            {subject.name.charAt(0).toUpperCase()}
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[var(--isp-heading)]">{subject.name}</h1>
            {subject.description && <p className="text-sm text-[var(--isp-body)] mt-0.5">{subject.description}</p>}
            {subject.lecturer_name && <p className="text-xs text-[var(--isp-muted)]">Prof. {subject.lecturer_name}</p>}
          </div>
        </div>
      </div>

      {/* Pillar #10 — live class network rollup */}
      <ClassNetworkStrip subjectId={subjectId} subjectName={subject.name} />

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="subject-stats">
        {[
          { label: 'Lectures', value: stats.lecture_count, icon: GraduationCap, color: 'from-blue-500 to-cyan-500' },
          { label: 'Flashcards', value: stats.flashcard_count, icon: Brain, color: 'from-purple-500 to-indigo-500' },
          { label: 'Quizzes', value: stats.quiz_count, icon: Target, color: 'from-amber-500 to-orange-500' },
          { label: 'Due Reviews', value: stats.due_reviews, icon: RotateCcw, color: 'from-rose-500 to-pink-500' },
        ].map((s, i) => (
          <motion.div key={s.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
            className="isp-card p-4 text-center" data-testid={`stat-${s.label.toLowerCase().replace(' ', '-')}`}>
            <div className={`w-9 h-9 mx-auto rounded-lg bg-gradient-to-br ${s.color} flex items-center justify-center mb-2`}>
              <s.icon className="w-4 h-4 text-white" />
            </div>
            <p className="text-2xl font-bold text-[var(--isp-heading)]">{s.value}</p>
            <p className="text-[10px] text-[var(--isp-muted)] uppercase tracking-wider">{s.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Second Row: Study Time + Avg Score + Storage */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="isp-card p-4 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-emerald-500/10"><Clock className="w-5 h-5 text-emerald-500" /></div>
          <div>
            <p className="text-lg font-bold text-[var(--isp-heading)]">{hours}h {mins}m</p>
            <p className="text-[10px] text-[var(--isp-muted)]">Total Study Time</p>
          </div>
        </div>
        <div className="isp-card p-4 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-amber-500/10"><Trophy className="w-5 h-5 text-amber-500" /></div>
          <div>
            <p className="text-lg font-bold text-[var(--isp-heading)]">{stats.avg_exam_score}%</p>
            <p className="text-[10px] text-[var(--isp-muted)]">Avg Exam Score ({stats.exam_count} exams)</p>
          </div>
        </div>
        <div className="isp-card p-4 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-500/10"><Cloud className="w-5 h-5 text-[#002FA7]" /></div>
          <div>
            <p className="text-sm font-semibold text-[var(--isp-heading)]">{storage.provider ? storage.folder_name || storage.provider : 'Not linked'}</p>
            <p className="text-[10px] text-[var(--isp-muted)]">{storage.provider ? 'Personal Storage' : 'No storage linked'}</p>
          </div>
        </div>
      </div>

      {/* Recent Lectures */}
      <div className="isp-card p-5" data-testid="recent-lectures-section">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-[var(--isp-heading)] flex items-center gap-2">
            <Mic className="w-4 h-4 text-[#002FA7]" /> Recent Lectures
          </h3>
          <Link to={`/lectures?subject=${subjectId}`} className="text-xs text-[#002FA7] hover:underline flex items-center gap-1">
            View All <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
        {recent_lectures.length === 0 ? (
          <p className="text-sm text-[var(--isp-muted)] text-center py-6">No lectures yet for this subject</p>
        ) : (
          <div className="space-y-2">
            {recent_lectures.map(l => (
              <Link key={l.id} to={`/lectures?preview=${l.id}`} className="flex items-center gap-3 p-3 rounded-xl hover:bg-[var(--isp-glass-inner)] transition-all">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: subject.color + '20' }}>
                  <FileText className="w-4 h-4" style={{ color: subject.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[var(--isp-heading)] truncate">{l.title}</p>
                  <p className="text-[10px] text-[var(--isp-muted)]">{l.date ? new Date(l.date).toLocaleDateString() : ''}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-[var(--isp-muted)]" />
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Recent Exams */}
      {recent_exams.length > 0 && (
        <div className="isp-card p-5" data-testid="recent-exams-section">
          <h3 className="text-sm font-semibold text-[var(--isp-heading)] flex items-center gap-2 mb-4">
            <Target className="w-4 h-4 text-amber-500" /> Recent Exam Scores
          </h3>
          <div className="space-y-2">
            {recent_exams.map((e, i) => {
              const pct = e.total > 0 ? Math.round(e.score / e.total * 100) : 0;
              return (
                <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-[var(--isp-glass-inner)]">
                  <div className={`text-sm font-bold ${pct >= 70 ? 'text-emerald-500' : pct >= 50 ? 'text-amber-500' : 'text-red-500'}`}>
                    {pct}%
                  </div>
                  <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <div className={`h-full rounded-full ${pct >= 70 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-[10px] text-[var(--isp-muted)]">{e.score}/{e.total}</span>
                  <span className="text-[10px] text-[var(--isp-muted)]">{e.date ? new Date(e.date).toLocaleDateString() : ''}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="flex gap-2 flex-wrap" data-testid="subject-actions">
        <Link to={`/lectures?subject=${subjectId}`}>
          <Tip text="View all lectures for this subject">
            <Button className="rounded-xl gap-2 bg-[#002FA7] hover:bg-[#00227A]"><GraduationCap className="w-4 h-4" /> View Lectures</Button>
          </Tip>
        </Link>
        <Link to="/lectures">
          <Tip text="Record a new lecture for this subject">
            <Button variant="outline" className="rounded-xl gap-2 border-[var(--isp-glass-border)] text-[var(--isp-body)]"><Mic className="w-4 h-4" /> Record Lecture</Button>
          </Tip>
        </Link>
        <Link to="/exam-mode">
          <Tip text="Practice with AI-generated exam questions">
            <Button variant="outline" className="rounded-xl gap-2 border-[var(--isp-glass-border)] text-[var(--isp-body)]"><Target className="w-4 h-4" /> Exam Mode</Button>
          </Tip>
        </Link>
        {stats.due_reviews > 0 && (
          <Link to="/study-plan">
            <Tip text={`${stats.due_reviews} flashcards due for review`}>
              <Button variant="outline" className="rounded-xl gap-2 border-rose-500/30 text-rose-400"><RotateCcw className="w-4 h-4" /> {stats.due_reviews} Due Reviews</Button>
            </Tip>
          </Link>
        )}
      </div>

      {/* AI Study Schedule Optimizer */}
      <div className="isp-card p-5" data-testid="ai-schedule-optimizer">
        <h3 className="text-sm font-semibold text-[var(--isp-heading)] flex items-center gap-2 mb-4">
          <Sparkles className="w-4 h-4 text-amber-500" /> AI Study Schedule Optimizer
        </h3>
        <p className="text-xs text-[var(--isp-body)] mb-4">
          Generate a smart study plan that prioritizes your weak areas, spaces repetition, and builds up to exam day.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <div>
            <label className="text-[10px] text-[var(--isp-muted)] block mb-1">Exam Date *</label>
            <Input type="date" value={scheduleForm.exam_date}
              onChange={e => setScheduleForm(p => ({ ...p, exam_date: e.target.value }))}
              className="rounded-xl bg-[var(--isp-glass-inner)] border-[var(--isp-glass-border)] text-[var(--isp-heading)]"
              data-testid="schedule-exam-date" />
          </div>
          <div>
            <label className="text-[10px] text-[var(--isp-muted)] block mb-1">Exam Title</label>
            <Input value={scheduleForm.exam_title} placeholder={`${subject.name} Final`}
              onChange={e => setScheduleForm(p => ({ ...p, exam_title: e.target.value }))}
              className="rounded-xl bg-[var(--isp-glass-inner)] border-[var(--isp-glass-border)] text-[var(--isp-heading)]"
              data-testid="schedule-exam-title" />
          </div>
          <div>
            <label className="text-[10px] text-[var(--isp-muted)] block mb-1">Hours/Day</label>
            <Input type="number" min={0.5} max={8} step={0.5} value={scheduleForm.hours}
              onChange={e => setScheduleForm(p => ({ ...p, hours: parseFloat(e.target.value) || 2 }))}
              className="rounded-xl bg-[var(--isp-glass-inner)] border-[var(--isp-glass-border)] text-[var(--isp-heading)]"
              data-testid="schedule-hours" />
          </div>
        </div>
        <Button onClick={generateAISchedule} disabled={generating} className="rounded-xl gap-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600" data-testid="generate-schedule-btn">
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {generating ? 'Generating...' : 'Generate AI Schedule'}
        </Button>

        {/* Schedule Result */}
        {scheduleResult && (
          <div className="mt-4 p-4 rounded-xl bg-amber-500/5 border border-amber-500/20" data-testid="schedule-result">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-4 h-4 text-amber-500" />
              <p className="text-sm font-medium text-[var(--isp-heading)]">{scheduleResult.total_events} study sessions created</p>
            </div>
            <div className="flex gap-4 text-xs text-[var(--isp-body)] mb-3">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" /> Learn: {scheduleResult.phases?.learn} days</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> Review: {scheduleResult.phases?.review} days</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Practice: {scheduleResult.phases?.practice} days</span>
            </div>
            {scheduleResult.weak_areas?.length > 0 && (
              <p className="text-[10px] text-[var(--isp-muted)] mb-2">Weak areas prioritized: {scheduleResult.weak_areas.join(', ')}</p>
            )}
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {(scheduleResult.events || []).map((ev, i) => (
                <div key={i} className="flex items-center gap-2 text-xs p-1.5 rounded-lg bg-[var(--isp-glass-inner)]">
                  <span className="text-[var(--isp-muted)] w-20 shrink-0">{ev.date}</span>
                  <span className="text-[var(--isp-heading)] font-medium truncate">{ev.topic}</span>
                </div>
              ))}
              {scheduleResult.total_events > 10 && (
                <Link to="/schedule" className="text-[10px] text-[#002FA7] hover:underline">View full schedule →</Link>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubjectOverviewPage;
